import { TeamReferences, type TeamReference } from './TeamReferences';
import { TeamNoteExtras, type NoteExtrasData } from './TeamNoteExtras';
import { lazy, Suspense, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText, LayoutDashboard, Users, Video, type LucideIcon } from 'lucide-react';
import { http } from '../api/http';
import { useAuthStore } from '../store/auth';
const SharedBoard = lazy(() => import('./TeamBoard'));

// Contenu partagé ouvert depuis un message (note, réunion ou tableau), en consultation.
// L'espace « Équipe » (liste des contenus partagés, mentions) a été retiré le 21/10/2026 : on n'arrive
// plus ici que par « Ouvrir l'objet » dans la messagerie. Un lien vers un autre contenu l'ouvre à la place.

export type SharedKind = 'notes' | 'meetings' | 'boards';
type Detail = NoteExtrasData & {
  id: string;
  title: string;
  references?: TeamReference[];
  body?: string;
  agenda?: string;
  minutes?: string;
  decisions?: {
    id: string;
    title: string;
    body: string;
    reference?: TeamReference | null;
  }[];
  scene?: Parameters<typeof SharedBoard>[0]['scene'];
};

const KINDS: Record<SharedKind, { label: string; icon: LucideIcon }> = {
  notes: { label: 'Note partagée', icon: FileText },
  meetings: { label: 'Réunion', icon: Users },
  boards: { label: 'Tableau', icon: LayoutDashboard },
};

function TextBlock({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <div>
      {label && <p className="mb-1 text-eyebrow uppercase text-secondary">{label}</p>}
      <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-body">{children}</p>
    </div>
  );
}

export default function SharedContent({ initial }: { initial: { kind: SharedKind; id: string } }) {
  const user = useAuthStore((s) => s.user);
  const [{ kind, id }, setOpened] = useState(initial);
  const detail = useQuery({
    queryKey: ['team-detail', user?.id, kind, id],
    queryFn: async () => (await http.get<Detail>('/v1/workspace/' + kind + '/' + id)).data,
    gcTime: 0,
    refetchInterval: 10000,
  });
  const conference = useQuery({
    queryKey: ['team-conference', user?.id, id],
    queryFn: async () => (await http.get<{ state: string; url: string | null }>('/v1/meeting-conferences/' + id)).data,
    enabled: kind === 'meetings',
    refetchInterval: 15000,
    gcTime: 0,
    retry: false,
  });
  const open = (next: SharedKind, nextId: string) => setOpened({ kind: next, id: nextId });
  const current = KINDS[kind];
  const meet = kind === 'meetings' && !conference.isError && conference.data?.state === 'READY' && conference.data.url;

  return (
    <article className="space-y-5 rounded-card bg-white p-5 shadow-card sm:p-6">
      <p className="flex items-center gap-2 text-eyebrow uppercase text-secondary">
        <current.icon size={14} className="text-primary" aria-hidden />
        {current.label}
      </p>
      {detail.isError ? (
        <p role="alert" className="rounded-row bg-danger-light px-4 py-3 text-sm text-danger-dark">
          Contenu indisponible ou partage retiré.
        </p>
      ) : !detail.data || detail.isFetching ? (
        <p className="text-sm text-secondary">Vérification de l’accès…</p>
      ) : (
        <>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <h2 className="text-title-md text-heading">{detail.data.title}</h2>
            {meet && (
              <a
                className="inline-flex h-10 items-center gap-2 rounded-pill bg-btn-primary px-[18px] text-sm font-semibold text-white shadow-btn transition-all hover:-translate-y-0.5 hover:shadow-btn-hover"
                href={conference.data!.url!}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Video size={16} aria-hidden />
                Rejoindre Meet
              </a>
            )}
          </div>
          {detail.data.body && <TextBlock>{detail.data.body}</TextBlock>}
          {detail.data.agenda && <TextBlock label="Ordre du jour">{detail.data.agenda}</TextBlock>}
          {detail.data.minutes && <TextBlock label="Compte rendu">{detail.data.minutes}</TextBlock>}
          {kind === 'notes' && <TeamNoteExtras id={id} data={detail.data} />}
          <TeamReferences references={detail.data.references ?? detail.data.resolvedReferences ?? []} onOpen={open} />
          {detail.data.scene?.cards?.length ? (
            <TeamReferences references={detail.data.scene.cards.map((c) => c.reference)} onOpen={open} />
          ) : null}
          {!!detail.data.decisions?.length && (
            <div className="space-y-3">
              <p className="text-eyebrow uppercase text-secondary">Décisions</p>
              {detail.data.decisions.map((d) => (
                <section key={d.id} className="space-y-1.5 rounded-row border border-line p-4">
                  <h3 className="text-[15px] font-semibold text-heading">{d.title}</h3>
                  <TeamReferences references={d.reference ? [d.reference] : []} onOpen={open} />
                  <p className="whitespace-pre-wrap text-sm text-body">{d.body}</p>
                </section>
              ))}
            </div>
          )}
          {detail.data.scene && (
            <Suspense fallback={<p className="text-sm text-secondary">Chargement du tableau…</p>}>
              <SharedBoard scene={detail.data.scene} boardId={id} />
            </Suspense>
          )}
        </>
      )}
    </article>
  );
}
